// @flow
import React, {PureComponent} from 'react';
import {ScrollView, StyleSheet, View, Keyboard, Platform} from 'react-native';
import {Text} from 'trpc-react-native-ui';
import {Field} from 'redux-form';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {NavigationScreenProp} from '@react-navigation/compat';
import {
  GradientPage,
  FullButton,
  HoshiInput,
  Checkbox,
} from '../../../components/common';
import {PageTitle} from '../../../components/signUp';
import I18n from '../../../lib/i18n';
import pageStyle from '../styles';
import theme from '../../../theme-config';
import config from '../../../config';
import GenderRadioButtons from './components/GenderRadioButtons';
import IdTypeRadioButtons from './components/IdTypeRadioButtons';
import HoshiInputWithCustomPlaceholder from './components/HoshiInputWithCustomPlaceholder';
import FormatService from '../../../lib/format';
import type {IdType} from 'digicel-kaizen-library';

export default class ConfirmPersonalInfo extends PureComponent<Props, State> {
  state: State = {
    isManuallyEditing: true,
  };

  styles = getStyles();
  inputs: {[string]: Field} = {};

  componentDidMount() {
    const isManuallyEditing = !!this.props.route.params?.isManuallyEditing;
    this.setState({isManuallyEditing: isManuallyEditing.toString() === 'true'});
  }

  isCountryFieldDisplayed = () =>
    this.state.isManuallyEditing
      ? config.displayCountryInEditionModeOnConfirmPersonalInfo
      : true;

  getMandatoryInputsInCheckingOrder = (): Array<string> => {
    if (config.askPersonalInformationDuringOrderSim) {
      return [
        'givenName',
        'familyName',
        'birthdate',
        'passportID',
        'contactPhoneNumber',
        'country',
      ];
    }

    return [
      'fullName',
      'birthdate',
      'passportID',
      'street1',
      'postCode',
      'city',
      'stateOrProvince',
      'country',
    ];
  };

  formatIdType = (idType: IdType) => {
    const identityCard = I18n.t('SignUp.ConfirmPersonalInfo.idCard');
    const passport = I18n.t('SignUp.ConfirmPersonalInfo.passport');

    if (idType === 'IDENTITY_CARD' || idType === identityCard)
      return identityCard;
    if (idType === 'PASSPORT' || idType === passport) return passport;
    return I18n.t('SignUp.ConfirmPersonalInfo.id');
  };

  getComponentIfDefined = (inputName: string) =>
    this.inputs[inputName] && this.inputs[inputName].getRenderedComponent();

  focusOnComponent = (inputName: string) => {
    const componentIfDefined = this.getComponentIfDefined(inputName);
    if (componentIfDefined) {
      componentIfDefined.focus();
    }
  };

  focusOnInputByName = (name: string) => () => this.focusOnComponent(name);

  checkMissingInputsAndSubmit = () => {
    this.props.handleSubmit();
    for (let inputName of this.getMandatoryInputsInCheckingOrder()) {
      const componentIfDefined = this.getComponentIfDefined(inputName);
      if (!componentIfDefined || componentIfDefined.isEmpty()) {
        if (Platform.OS === 'android') {
          Keyboard.dismiss();
          setTimeout(() => this.focusOnComponent(inputName), 400); // handle focus if multiple missing inputs
        } else {
          this.focusOnComponent(inputName);
        }
        break;
      }
    }
  };

  formatGender = (value: string) => I18n.t(`gender.${value}`);

  renderGenderRadio = () => (
    <Field
      component={GenderRadioButtons}
      disabled={!this.state.isManuallyEditing}
      name="gender"
      style={this.styles.inputContainer}
    />
  );

  renderNameFields = () => {
    const commonFieldProps = {
      component: HoshiInput,
      disabled: !this.state.isManuallyEditing,
      returnKeyType: 'next',
      style: this.styles.inputContainer,
      forwardRef: true,
    };

    if (config.askPersonalInformationDuringOrderSim) {
      return (
        <>
          {config.genderFormatOnConfirmPersonalInfoPage === 'label' && (
            <Field
              format={this.formatGender}
              name="gender"
              onSubmitEditing={this.focusOnInputByName('gender')}
              ref={ref => (this.inputs.gender = ref)}
              {...commonFieldProps}
            />
          )}
          <Field
            name="givenName"
            onSubmitEditing={this.focusOnInputByName('givenName')}
            placeholder={I18n.t('givenName')}
            props={{placeholderTextColor: theme.errorColor}}
            ref={ref => (this.inputs.givenName = ref)}
            {...commonFieldProps}
          />
          <Field
            name="familyName"
            onSubmitEditing={this.focusOnInputByName('familyName')}
            placeholder={I18n.t('familyName')}
            props={{placeholderTextColor: theme.errorColor}}
            ref={ref => (this.inputs.familyName = ref)}
            {...commonFieldProps}
          />
        </>
      );
    } else {
      const onSubmitEditing = config.askMiddleName
        ? this.focusOnInputByName('middleName')
        : this.focusOnInputByName('birthdate');

      return (
        <Field
          name="fullName"
          onSubmitEditing={onSubmitEditing}
          placeholder={I18n.t('fullName')}
          props={{placeholderTextColor: theme.errorColor}}
          ref={ref => (this.inputs.fullName = ref)}
          {...commonFieldProps}
        />
      );
    }
  };

  renderMiddleNameField = () =>
    config.askMiddleName && (
      <Field
        component={HoshiInput}
        name="middleName"
        onSubmitEditing={this.focusOnInputByName('birthdate')}
        placeholder={I18n.t('middleName')}
        ref={ref => (this.inputs.middleName = ref)}
        returnKeyType="next"
        style={this.styles.inputContainer}
        forwardRef
      />
    );

  renderIdCardFields = () => {
    const styles = this.styles;
    const isManuallyEditing = this.state.isManuallyEditing;
    const containerStyle = config.askPersonalInformationDuringOrderSim
      ? {}
      : styles.horizontalContainer;
    const onSubmitIdCard = config.askPersonalInformationDuringOrderSim
      ? this.focusOnInputByName('contactPhoneNumber')
      : this.focusOnInputByName('street1');

    return (
      <View style={containerStyle}>
        <Field
          component={isManuallyEditing ? IdTypeRadioButtons : HoshiInput}
          disabled={!isManuallyEditing}
          format={!isManuallyEditing && this.formatIdType}
          name="identification.identificationType"
          style={styles.inputContainer}
        />
        <Field
          component={HoshiInput}
          disabled={!isManuallyEditing}
          name="identification.identificationId"
          onSubmitEditing={onSubmitIdCard}
          placeholder={I18n.t('SignUp.ConfirmPersonalInfo.idNumber')}
          props={{placeholderTextColor: theme.errorColor}}
          ref={ref => (this.inputs.passportID = ref)}
          returnKeyType="next"
          style={styles.inputContainer}
          testID={'identificationId'}
          forwardRef
        />
      </View>
    );
  };

  renderAddressFields = () => {
    const styles = this.styles;

    if (config.askPersonalInformationDuringOrderSim) {
      return null;
    }

    return (
      <>
        <Field
          component={HoshiInput}
          name="idAddress.street1"
          onSubmitEditing={this.focusOnInputByName('street2')}
          placeholder={I18n.t('SignUp.DeliveryAddress.form.street1')}
          ref={ref => (this.inputs.street1 = ref)}
          returnKeyType="next"
          style={styles.inputContainer}
          forwardRef
        />
        <Field
          component={HoshiInput}
          name="idAddress.street2"
          onSubmitEditing={this.focusOnInputByName('postCode')}
          placeholder={I18n.t('SignUp.DeliveryAddress.form.street2')}
          ref={ref => (this.inputs.street2 = ref)}
          returnKeyType="next"
          style={styles.inputContainer}
          forwardRef
        />
        <View style={styles.horizontalContainer}>
          <Field
            component={HoshiInput}
            name="idAddress.postCode"
            onSubmitEditing={this.focusOnInputByName('city')}
            placeholder={I18n.t('SignUp.DeliveryAddress.form.postCode')}
            ref={ref => (this.inputs.postCode = ref)}
            returnKeyType="next"
            style={[styles.postCode, styles.inputContainer]}
            forwardRef
          />
          <Field
            component={HoshiInput}
            name="idAddress.city"
            onSubmitEditing={this.focusOnInputByName('stateOrProvince')}
            placeholder={I18n.t('SignUp.DeliveryAddress.form.city')}
            ref={ref => (this.inputs.city = ref)}
            returnKeyType="next"
            style={[styles.city, styles.inputContainer]}
            forwardRef
          />
        </View>
        <Field
          component={HoshiInput}
          name="idAddress.stateOrProvince"
          onSubmitEditing={this.checkMissingInputsAndSubmit}
          placeholder={I18n.t('SignUp.DeliveryAddress.form.region')}
          ref={ref => (this.inputs.stateOrProvince = ref)}
          returnKeyType="go"
          style={styles.inputContainer}
          forwardRef
        />
      </>
    );
  };

  renderPhoneNumberField = () => {
    const styles = this.styles;
    if (!config.askPersonalInformationDuringOrderSim) {
      return null;
    }

    return (
      <Field
        component={HoshiInput}
        keyboardType="phone-pad"
        name="contactPhoneNumber"
        placeholder={I18n.t('SignUp.DeliveryContact.phoneNumber')}
        props={{placeholderTextColor: theme.errorColor}}
        ref={ref => (this.inputs.contactPhoneNumber = ref)}
        returnKeyType={'go'}
        style={styles.inputContainer}
        testID={'confirmPersonalInfoPhoneNumber'}
        forwardRef
      />
    );
  };

  renderBillingAddressCheckbox = () => (
    <>
      {!config.askPersonalInformationDuringOrderSim && (
        <Field
          component={({input: {value, onChange}}) => (
            <Checkbox
              isChecked={value}
              lightColors
              onPress={() => onChange(!value)}
              text={I18n.t('SignUp.DeliveryAddress.useAsBilling')}
            />
          )}
          name="useAddressAsBilling"
        />
      )}
    </>
  );

  renderCountryField = () => {
    const styles = this.styles;
    const isCountryFieldDisplayed = this.isCountryFieldDisplayed();

    if (!isCountryFieldDisplayed) {
      this.inputs.country = null;
      return null;
    }

    return (
      <Field
        component={HoshiInput}
        disabled={!this.state.isManuallyEditing}
        name="nationality"
        onSubmitEditing={this.focusOnInputByName('passportID')}
        placeholder={I18n.t('country')}
        ref={ref => (this.inputs.country = ref)}
        returnKeyType="next"
        style={[styles.inputContainer, styles.country]}
        forwardRef
      />
    );
  };

  enableManualEdition = () => {
    this.setState({isManuallyEditing: true});
  };

  render() {
    const {selectedGender, route, isLoading, invalid, submitting, pristine} = this.props;
    const styles = this.styles;
    const isCountryFieldDisplayed = this.isCountryFieldDisplayed();

    const isScanSuccessful = route.params?.isScanSuccessful;
    const isScanSuccessfulLabelPath = `SignUp.ConfirmPersonalInfo.subtitle.isScanSuccessful.${isScanSuccessful}`;
    const subtitleLabelPath = I18n.has(isScanSuccessfulLabelPath)
      ? isScanSuccessfulLabelPath
      : 'SignUp.ConfirmPersonalInfo.subtitle.default';

    return (
      <GradientPage iosEdges={['top','bottom']} maxWidth={600}>

          <PageTitle
            title={I18n.t('SignUp.ConfirmPersonalInfo.title')}
            style={[pageStyle.title, styles.title]}
            subtitle={I18n.t(subtitleLabelPath)}
          />

          <KeyboardAwareScrollView
            keyboardShouldPersistTaps="never"
            enableOnAndroid="true">
            <ScrollView style={[pageStyle.form, styles.form]}>
              <View>
                {this.renderIdCardFields()}
                {config.genderFormatOnConfirmPersonalInfoPage === 'radio' &&
                  this.renderGenderRadio()}
                {this.renderNameFields()}
                {selectedGender === 'FEMALE' && this.renderMiddleNameField()}
                <View
                  style={isCountryFieldDisplayed && styles.horizontalContainer}>
                  <Field
                    component={HoshiInputWithCustomPlaceholder}
                    errorNumberOfLines={2}
                    errorStyle={isCountryFieldDisplayed && styles.birthDate}
                    keyboardType={Platform.OS === 'web' ? '' : 'phone-pad'}
                    maxLength={10}
                    name="birthdate"
                    normalize={FormatService.formatBirthdate}
                    onSubmitEditing={this.focusOnInputByName('country')}
                    props={{placeholderTextColor: theme.errorColor}}
                    ref={ref => (this.inputs.birthdate = ref)}
                    returnKeyType="next"
                    style={[
                      styles.inputContainer,
                      isCountryFieldDisplayed && styles.birthDate,
                    ]}
                    forwardRef
                  />
                  {this.renderCountryField()}
                </View>
                {this.renderAddressFields()}
                {this.renderPhoneNumberField()}
              </View>
            {this.renderBillingAddressCheckbox()}
          </ScrollView>
        </KeyboardAwareScrollView>
        <View style={styles.bottomPanel}>
          {config.displayConfirmPersonalInformationText && (
            <Text style={styles.confirmText}>
              {I18n.t('SignUp.ConfirmPersonalInfo.certifyDetails')}
            </Text>
          )}
          <FullButton
            onPress={this.checkMissingInputsAndSubmit}
            isLoading={isLoading}
            testID={'confirmInfoPageButton'}
            text={I18n.t('confirm')}
            disabled={invalid || submitting || pristine}
          />
          {!this.state.isManuallyEditing && (
            <Text
              onPress={this.enableManualEdition}
              style={styles.manualInputText}>
              {I18n.t('SignUp.ConfirmPersonalInfo.manualInput')}
            </Text>
          )}
        </View>
      </GradientPage>
    );
  }
}

type State = {
  isManuallyEditing: boolean,
};

export type StateProps = {
  initialValues: Object,
  selectedGender: string,
};

export type DispatchProps = {
  hasEditedIdentificationInfo: Function,
  confirmPersonalInfo: Function,
  setDeliveryContact: Function,
};

export type MergeProps = {
  onSubmit: Function,
};

type Props = NavigationScreenProp & StateProps & DispatchProps & MergeProps;

const getStyles = () =>
  StyleSheet.create({
    birthDate: {
      width: 145,
    },
    bottomPanel: {
      borderStyle: 'solid',
      borderTopColor: theme.signUpTextColor,
      borderTopWidth: 1,
      justifyContent: 'center',
      paddingHorizontal: 20,
      paddingVertical: 13,
    },
    city: {
      width: 175,
    },
    confirmText: {
      color: theme.signUpDarkTextColor,
      marginBottom: 13,
    },
    country: {
      width: 100,
    },
    form: {
      marginTop: 0,
    },
    horizontalContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    inputContainer: {
      marginVertical: 8,
    },
    passportID: {
      width: 180,
    },
    postCode: {
      width: 95,
    },
    manualInputText: {
      fontSize: 16,
      color: theme.primaryColor,
      fontWeight: 'bold',
      marginTop: 20,
      textAlign: 'center',
    },
    secondaryButton: {
      marginTop: 8,
    },
    subtitle: {
      fontSize: 16,
      lineHeight: 25,
      marginVertical: 25,
    },
    subtitleWithBullet: {
      paddingHorizontal: 20,
      textAlign: 'left',
    },
    title: {
      fontWeight: 'bold',
    },
  });
